import { useSearchCustomers } from "api/customers/service";
import React, { useMemo, useEffect, useState, useRef } from "react";
import { Row, Col, FormGroup, Label, Input, Button, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatetime from "components/CustomDatePicker";
import { formatDateForPicker } from "utils/dateUtils";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

function IdentityInformationForm({
  identityData,
  onChange,
  showValidation = true,
  relation = false,
  handleSubmit,
  title,
  showMatches = true,
  onCustomerSelect,
  activeColor = "blue",
  isTheme2 = false,
  selectStyles: selectStylesProp,
  theme2SelectMenuClassName,
}) {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () =>
      selectStylesProp ??
      getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [selectStylesProp, activeColorStyle, isTheme2]
  );
  const theme2SelectProps = theme2SelectMenuClassName
    ? {
        classNames: {
          menu: () => theme2SelectMenuClassName,
          menuPortal: () => theme2SelectMenuClassName,
        },
      }
    : {};
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
  const dateInputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0 };

  // Set default title if not provided
  const formTitle = title || t('identityInformationForm.title');
  // Helper function to safely check if a field has a value (handles Date objects, strings, null, undefined)
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (value instanceof Date) return true;
    if (typeof value === 'string') return value.trim() !== '';
    return Boolean(value);
  };

  const isValid = hasValue(identityData?.full_name);

  // Debounced search terms (500ms)
  const [nameTerm, setNameTerm] = useState('');
  const [ssnTerm, setSsnTerm] = useState('');
  const nameTimer = useRef(null);
  const ssnTimer = useRef(null);

  useEffect(() => {
    if (!showMatches) return;
    clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => {
      setNameTerm(identityData?.full_name?.trim() || '');
    }, 500);
    return () => clearTimeout(nameTimer.current);
  }, [identityData?.full_name, showMatches]);

  useEffect(() => {
    if (!showMatches) return;
    clearTimeout(ssnTimer.current);
    ssnTimer.current = setTimeout(() => {
      setSsnTerm(identityData?.ssn?.trim() || '');
    }, 500);
    return () => clearTimeout(ssnTimer.current);
  }, [identityData?.ssn, showMatches]);

  const { data: customersData, isFetching } = useSearchCustomers(
    { name: nameTerm, ssn: ssnTerm },
    showMatches
  );

  const filteredCustomers = useMemo(() => {
    if (!customersData?.results) return [];
    return customersData.results;
  }, [customersData]);

  const hasSearchTerm = nameTerm.length >= 2 || ssnTerm.length >= 2;

  const handleCustomerSelect = (customerId) => {
    const selectedCustomer = filteredCustomers.find(c => c.id === customerId);
    onChange({ target: { name: 'new_customer', value: false } });
    onChange({ target: { name: 'customer_id', value: customerId } });
    if (selectedCustomer) {
      onChange({ target: { name: 'full_name', value: selectedCustomer.full_name } });
      onChange({ target: { name: 'ssn', value: selectedCustomer.ssn || '' } });
      onChange({ target: { name: 'date_of_birth', value: selectedCustomer.date_of_birth || '' } });
      onChange({ target: { name: 'gender', value: selectedCustomer.gender || '' } });
    }
    if (onCustomerSelect) {
      onCustomerSelect(customerId, selectedCustomer);
    } else {
      handleSubmit();
    }
  };

  return (
    <>
      {showValidation && !isValid && (
        <Row>
          <Col md="12">
            <div className="alert alert-warning mb-0 rounded-0">
              <strong>{t('identityInformationForm.identityInformationRequired')}</strong> {t('identityInformationForm.fullNameDOBOrSSN')}
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col md="12">
          <h5 className={`mb-3 ${showValidation ? 'mt-2' : 'mt-0'}`}>{formTitle}</h5>
        </Col>
      </Row>
      {relation && (
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="relationship_type">{t('identityInformationForm.relationship')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                {...theme2SelectProps}
                placeholder={t('identityInformationForm.selectRelationship')}
                name="relationship_type"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={identityData.relationship_type ? {
                  value: identityData.relationship_type,
                  label: identityData.relationship_type
                } : null}
                onChange={(option) => {
                  const syntheticEvent = {
                    target: {
                      name: 'relationship_type',
                      value: option?.value || ''
                    }
                  };
                  onChange(syntheticEvent);
                }}
                options={[
                  { value: "", label: t('identityInformationForm.selectRelationship') },
                  { value: "Aunt", label: t('identityInformationForm.aunt') },
                  { value: "Brother", label: t('identityInformationForm.brother') },
                  { value: "Child", label: t('identityInformationForm.child') },
                  { value: "Cousin", label: t('identityInformationForm.cousin') },
                  { value: "Daughter", label: t('identityInformationForm.daughter') },
                  { value: "Father", label: t('identityInformationForm.father') },
                  { value: "Friend", label: t('identityInformationForm.friend') },
                  { value: "Grandchild", label: t('identityInformationForm.grandchild') },
                  { value: "Granddaughter", label: t('identityInformationForm.granddaughter') },
                  { value: "Grandfather", label: t('identityInformationForm.grandfather') },
                  { value: "Grandmother", label: t('identityInformationForm.grandmother') },
                  { value: "Grandparent", label: t('identityInformationForm.grandparent') },
                  { value: "Grandson", label: t('identityInformationForm.grandson') },
                  { value: "Husband", label: t('identityInformationForm.husband') },
                  { value: "In-Law", label: t('identityInformationForm.inLaw') },
                  { value: "Mother", label: t('identityInformationForm.mother') },
                  { value: "Mother In-Law", label: t('identityInformationForm.motherInLaw') },
                  { value: "Nephew", label: t('identityInformationForm.nephew') },
                  { value: "Niece", label: t('identityInformationForm.niece') },
                  { value: "Other", label: t('identityInformationForm.other') },
                  { value: "Parent", label: t('identityInformationForm.parent') },
                  { value: "Relative", label: t('identityInformationForm.relative') },
                  { value: "Sibling", label: t('identityInformationForm.sibling') },
                  { value: "Sister", label: t('identityInformationForm.sister') },
                  { value: "Sister In-Law", label: t('identityInformationForm.sisterInLaw') },
                  { value: "Son", label: t('identityInformationForm.son') },
                  { value: "Son In-Law", label: t('identityInformationForm.sonInLaw') },
                  { value: "Spouse", label: t('identityInformationForm.spouse') },
                  { value: "Uncle", label: t('identityInformationForm.uncle') },
                  { value: "Unknown", label: t('identityInformationForm.unknown') },
                  { value: "Wife", label: t('identityInformationForm.wife') }
                ]}
                isClearable
              />
            </FormGroup>
          </Col>
        </Row>
      )}
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="full_name">{t('identityInformationForm.fullName')}</Label>
            <Input
              type="text"
              name="full_name"
              id="full_name"
              value={identityData.full_name}
              onChange={onChange}
              className="form-control"
              style={inputStyle}
              placeholder={t('identityInformationForm.enterFullName')}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="ssn">{t('identityInformationForm.ssn')}</Label>
            <Input
              type="text"
              name="ssn"
              id="ssn"
              value={identityData.ssn}
              onChange={onChange}
              className="form-control"
              style={inputStyle}
              placeholder={t('identityInformationForm.enterSSN')}
            />
          </FormGroup>
        </Col>
      </Row>


      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="date_of_birth">{t('identityInformationForm.dateOfBirth')}</Label>
            <ReactDatetime
              inputProps={{
                className: "form-control",
                placeholder: t('identityInformationForm.selectDate'),
                style: dateInputStyle,
                onInput: (e) => {
                  // Input'a her karakter girildiğinde kontrol et
                  const value = e.target.value;
                  // Sadece rakamları al (tireleri kaldır)
                  const numbersOnly = value.replace(/[^0-9]/g, '');

                  // Maksimum 8 rakama ulaşıldıysa, eski değeri koru (yeni karakter ekleme)
                  if (numbersOnly.length > 8) {
                    const previousNumbers = numbersOnly.substring(0, 8);
                    // Önceki geçerli formatı geri yükle
                    let formatted = previousNumbers.substring(0, 2);
                    if (previousNumbers.length > 2) {
                      formatted += '-' + previousNumbers.substring(2, 4);
                      if (previousNumbers.length > 4) {
                        formatted += '-' + previousNumbers.substring(4, 8);
                      }
                    }
                    e.target.value = formatted;
                    // İmleci sonuna al
                    setTimeout(() => {
                      e.target.setSelectionRange(formatted.length, formatted.length);
                    }, 0);
                    return;
                  }

                  // Otomatik formatla: MM-DD-YYYY
                  let formatted = '';
                  if (numbersOnly.length > 0) {
                    // İlk 2 rakam: MM
                    formatted = numbersOnly.substring(0, 2);

                    if (numbersOnly.length > 2) {
                      // Tire ekle ve sonraki 2 rakam: DD
                      formatted += '-' + numbersOnly.substring(2, 4);

                      if (numbersOnly.length > 4) {
                        // Tire ekle ve sonraki 4 rakam: YYYY
                        formatted += '-' + numbersOnly.substring(4, 8);
                      }
                    }
                  }

                  e.target.value = formatted;
                },
                onBlur: (e) => {
                  const inputValue = e.target.value;
                  // Input'tan çıkıldığında, eğer geçersiz bir değer varsa bugünün tarihini set et
                  if (inputValue && inputValue.length > 0 && inputValue.length !== 10) {
                    onChange({ target: { name: "date_of_birth", value: new Date() } });
                  }
                }
              }}
              value={identityData.date_of_birth ? formatDateForPicker(identityData.date_of_birth) : identityData.date_of_birth}
              onChange={(date) => {
                // Sadece geçerli tarih objelerini kabul et
                if (date && date._isAMomentObject && date.isValid()) {
                  // Takvimden seçim veya geçerli manuel giriş - Date objesine çevir
                  onChange({ target: { name: "date_of_birth", value: date.toDate() } });
                } else if (typeof date === 'string' && date.length === 0) {
                  // Input temizlenirse state'i de temizle
                  onChange({ target: { name: "date_of_birth", value: "" } });
                }
                // Geçersiz girişler ignore edilir
              }}
              dateFormat="MM-DD-YYYY"
              timeFormat={false}
              closeOnSelect={true}
              strictParsing={true}
              isValidDate={(current) => {
                // Bugünden sonraki tarihleri engelle
                return current.isBefore(new Date());
              }}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="gender">{t('identityInformationForm.gender')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('identityInformationForm.selectGender')}
              name="gender"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={identityData.gender ? {
                value: identityData.gender,
                label: identityData.gender === "Male" ? t('identityInformationForm.male') : t('identityInformationForm.female')
              } : null}
              onChange={(option) => {
                onChange({
                  target: {
                    name: "gender",
                    value: option?.value || ""
                  }
                });
              }}
              options={[
                { value: "Male", label: t('identityInformationForm.male') },
                { value: "Female", label: t('identityInformationForm.female') }
              ]}
              isClearable
            />
          </FormGroup>
        </Col>
      </Row>
      {/* Display filtered customers in Table */}
      {showMatches && hasSearchTerm && isFetching && (
        <Row>
          <Col md="12">
            <div
              className="d-flex justify-content-center align-items-center mb-3"
              style={{ minHeight: "100px" }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: getActiveColorStyle(activeColor).borderColor }}
              >
                <span className="sr-only">Searching...</span>
              </div>
            </div>
          </Col>
        </Row>
      )}
      {showMatches && filteredCustomers.length > 0 && (
        <Row>
          <Col md="12">
            <div className="alert alert-info mb-3">
              <strong>{t('identityInformationForm.matchingCustomers')} ({filteredCustomers.length}):</strong>
            </div>
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th className="text-center">#</th>
                  <th>{t('identityInformationForm.fullName')}</th>
                  <th>{t('identityInformationForm.ssn')}</th>
                  <th className="text-right">{t('identityInformationForm.action')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, idx) => (
                  <tr key={customer.id}>
                    <td className="text-center">{idx + 1}</td>
                    <td>{customer.full_name || 'N/A'}</td>
                    <td>{customer.ssn || 'N/A'}</td>
                    <td className="text-right">
                      <div className="actions-right">
                        <Button
                          color="blue"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerSelect(customer.id);
                          }}
                        >
                          <i className="tim-icons icon-simple-add" /> {t('identityInformationForm.select')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}

      <Row>
        <Col md="12">
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                name="active_service_member"
                id="active_service_member"
                checked={identityData.active_service_member}
                onChange={onChange}
              />
              <span className="form-check-sign" />
              {t('identityInformationForm.activeServiceMember')}
            </Label>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

export default IdentityInformationForm;

